import { debounce, isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddInIds from "@/constants/AddInIds";
import { GEOTAB_GROUP_NAMES } from "@/constants/groupConfig";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { useGroupPermission } from "@/hooks/groups/useGroupPermission";
import {
  MapGroupStyle,
  MapGroupStyleAddInData,
  createMapGroupStyleRecord,
  isMapGroupStyleRecord,
} from "@/models/MapGroupStyleAddInData";

export interface MapGroupStylesState {
  /** In priority order: a vehicle in two of these takes the first one's colour. */
  groups: MapGroupStyle[];
  /** Whether the shared record is known yet. */
  isFetched: boolean;
  /** Whether this user may change how the map looks for everybody. */
  canEdit: boolean;
  /** Adds a group or changes one, keeping the order. Ignored without permission. */
  setGroupStyle: (groupId: string, patch: Partial<Omit<MapGroupStyle, "groupId">>) => void;
  /** Takes a group out entirely, colour and symbol together. Ignored without permission. */
  removeGroup: (groupId: string) => void;
  /** Replaces the whole list in one write. Ignored without permission. */
  setGroups: (next: MapGroupStyle[]) => void;
}

/**
 * How long a change waits before it is written.
 *
 * A colour picker reports a new colour on every movement of the pointer, and this is one shared
 * record: written each time, the second write starts before the first has come back, refers to
 * the row the first one optimistically put there, and fails -- so the colour the user chose
 * quietly did not stick.
 */
const PERSIST_DELAY_MS = 400;

/**
 * How each Geotab group's vehicles are drawn — one record, shared by everybody.
 *
 * Not a personal setting: the point of colouring the workshop's vehicles is that everyone looking
 * at the map sees the same thing. Writing it therefore needs the {@link GEOTAB_GROUP_NAMES.MAP_ADMIN}
 * permission; reading it needs nothing.
 */
export function useMapGroupStyles(): MapGroupStylesState {
  const { granted: canEdit } = useGroupPermission(GEOTAB_GROUP_NAMES.MAP_ADMIN);

  const { data: records = [], isFetched } = useAddinData<unknown>(AddInIds.mapGroupStyles);
  const { mutate: persist } = useUpdateAddIn<MapGroupStyleAddInData>(
    AddInIds.mapGroupStyles,
    "id",
  );

  const stored = useMemo(() => {
    const record = records.find(isMapGroupStyleRecord);
    if (!record) {
      return [];
    }
    // Only entries that still make sense: a record written by another version, or by hand, must
    // not put `undefined` into a colour accessor.
    return record.groups.filter(group => !!group?.groupId);
  }, [records]);

  /** What the user has just chosen, until the stored record catches up and says the same. */
  const [pending, setPending] = useState<MapGroupStyle[] | null>(null);
  const groups = pending ?? stored;

  useEffect(() => {
    if (pending && isEqual(pending, stored)) {
      setPending(null);
    }
  }, [pending, stored]);

  const persistDebounced = useMemo(
    () => debounce(persist, PERSIST_DELAY_MS, { leading: false, trailing: true }),
    [persist],
  );

  // A change made in the last moments before the panel closes would otherwise be dropped.
  const flushRef = useRef(persistDebounced);
  flushRef.current = persistDebounced;
  useEffect(() => () => flushRef.current.flush(), []);

  const save = useCallback(
    (next: MapGroupStyle[]) => {
      if (!canEdit) {
        return;
      }
      // Shown straight away; the record follows once the pointer stops moving.
      setPending(next);
      persistDebounced(createMapGroupStyleRecord(next));
    },
    [canEdit, persistDebounced],
  );

  const setGroupStyle = useCallback(
    (groupId: string, patch: Partial<Omit<MapGroupStyle, "groupId">>) => {
      const existing = groups.find(group => group.groupId === groupId);
      save(
        existing
          ? groups.map(group => (group.groupId === groupId ? { ...group, ...patch } : group))
          : // Appended rather than prepended, so adding a group does not silently outrank the
            // ones already there.
            [...groups, { groupId, color: "", ...patch }],
      );
    },
    [groups, save],
  );

  const removeGroup = useCallback(
    (groupId: string) => save(groups.filter(group => group.groupId !== groupId)),
    [groups, save],
  );

  return { groups, isFetched, canEdit, setGroupStyle, removeGroup, setGroups: save };
}
